import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  InputBase,
  debounce,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import {
  updateLeadOwner,
  getUserByEmail,
  ILead,
  usersAdvanceFilterQuery,
  LogContextType,
  saveActivityLog,
  LogAction,
} from 'wisestep-api';
import { WsButton, WsDrawer } from 'wisestep-base';

import { getRecruiterNameAndEmail } from '../../..//features/jobs/helper';
import { IOption } from '../../../components/SettingsIndex';
import { StyledFooter } from '../../../features/jobs/assign/types';
import { success, failed, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { defaultLeadActivityLogDTO, shareClientAndContact } from '../helper/leadHelper';

interface BulkDrawerProps {
  open: boolean;
  selectedLeadIds: string[];
  onClose: () => void;
  onBulkUpdate: () => void;
  leadsData: ILead[];
}

const BulkLeadOwnerUpdateDrawer = ({
  open,
  selectedLeadIds,
  onClose,
  onBulkUpdate,
  leadsData,
}: BulkDrawerProps) => {
  const formMethods = useForm();
  const [isLoading, setLoading] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [recruitersOptions, setRecruitersOptions] = useState<IOption[]>([]);
  const initialRecruitersRef = useRef<IOption[]>([]);
  const abortControllerRef = useRef(new AbortController());
  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const response = await usersAdvanceFilterQuery('');
        const recruitersList = getRecruiterNameAndEmail(response.data);
        const options = recruitersList.map((item) => ({
          label: item.text,
          value: item.email,
        }));
        setRecruitersOptions(options);
      } catch (error) {
        console.error('Error fetching recruiters:', error);
      }
    };
    fetchRecruiters();
  }, []);

  useEffect(() => {
    if (open) {
      formMethods.reset({ recruiterIds: [] });
    }
  }, [open]);

  const onUserInputKey = async (searchText: string) => {
    if (searchText?.length > 2) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;
      try {
        const query = encodeURIComponent(
          searchText
            .trim()
            .split(' ')
            .map((name) => (isEmpty(name) ? '' : name + ':*'))
            .join(' & ')
        );
        const response = await usersAdvanceFilterQuery(query);
        const recruitersList = getRecruiterNameAndEmail(response.data);
        const options = recruitersList.map((item) => ({
          label: item.text,
          value: item.email,
        }));
        setRecruitersOptions(options);
      } catch (error) {
        console.error('Error fetching recruiters:', error);
      }
    }
  };

  const debouncedOnUserInputKey = useMemo(() => debounce(onUserInputKey, 150), []);

  const debouncedUsersInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    if (isEmpty(searchText)) {
      setRecruitersOptions(initialRecruitersRef.current);
    } else {
      debouncedOnUserInputKey(searchText);
    }
  };

  const onSubmit = async (data: any) => {
    if (!data.recruiterIds?.[0]) {
      setNotification(warning('Select a Valid Active User'));
      return;
    }
    setLoading(true);
    try {
      const recruiter = (await getUserByEmail(data.recruiterIds[0])).data;
      const newOwnerName = `${recruiter?.firstName} ${recruiter?.lastName}`;

      await Promise.all(
        selectedLeadIds.map(async (id) => {
          const currentLead = leadsData.find((l) => l.id === id);
          const oldOwner = currentLead?.leadOwnerInfo?.name || ' ';
          const leadName = currentLead?.leadName || 'Unknown Lead';

          await updateLeadOwner({
            id,
            leadOwnerInfo: {
              id: recruiter?.email,
              name: newOwnerName,
            },
          } as ILead);

          shareClientAndContact(currentLead, recruiter);

          const logPayload = {
            ...defaultLeadActivityLogDTO,
            contextData: {},
            action: LogAction.Edit,
            field: 'Lead Owner',
            description: `updated Lead Owner from ${oldOwner} to ${newOwnerName} for ${leadName}`,
            entityId: id,
            entityName: leadName,
            parentEntitiyType: LogContextType.Lead,
            parentEntitiyId: id,
          };

          await saveActivityLog(logPayload);
        })
      );

      setNotification(success('Lead Owner Updated Successfully for selected leads!'));
      onBulkUpdate();
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Bulk update error:', error);
      setNotification(failed('Something went wrong, Please try again !!'));
    }
    setLoading(false);
  };

  return (
    <WsDrawer
      open={open}
      onClose={onClose}
      size="small"
      title="Assign Lead Owner"
      headerSize="medium"
    >
      <Box width="100%" role="presentation" p={1.5}>
        <FormProvider {...formMethods}>
          <Grid container direction="row" spacing={1} alignItems="center">
            <Grid item>
              <SearchIcon />
            </Grid>
            <Grid item xs>
              <InputBase
                placeholder="Search User"
                name="recruiterIds"
                onChange={debouncedUsersInput}
                fullWidth
              />
            </Grid>
          </Grid>
          <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <Controller
                name="recruiterIds"
                control={formMethods.control}
                defaultValue={[]}
                render={({ field: { value, onChange } }) => (
                  <FormGroup>
                    {recruitersOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={value.includes(option.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                onChange([option.value]);
                              } else {
                                onChange([]);
                              }
                            }}
                            disabled={value.length === 1 && !value.includes(option.value)}
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </FormGroup>
                )}
              />
            </FormControl>
            <StyledFooter>
              <Grid container item justifyContent="right" py={1}>
                <WsButton
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
                >
                  {isLoading ? 'Assigning...' : 'Assign'}
                </WsButton>
              </Grid>
            </StyledFooter>
          </Box>
        </FormProvider>
      </Box>
    </WsDrawer>
  );
};

export default BulkLeadOwnerUpdateDrawer;
